import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Star, Pill, BookOpen, AlertTriangle } from "lucide-react";
import GuidelineReviewNotice from "../components/GuidelineReviewNotice.jsx";
import ReviewStatusBadge from "../components/ReviewStatusBadge.jsx";

const sevClass = (s) => {
  const k = (s || "").toLowerCase();
  if (k.includes("contraindic")) return "bg-rose-200 text-rose-900 border-rose-300";
  if (k.includes("severe") || k.includes("major")) return "bg-rose-100 text-rose-800 border-rose-200";
  if (k.includes("moderate")) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
};

function Field({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-1">{label}</div>
      <div className="text-sm text-slate-800">{typeof value === "string" ? value : JSON.stringify(value)}</div>
    </div>
  );
}

function BulletList({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="space-y-1 text-sm text-slate-700">
      {items.map((x, i) => (
        <li key={i} className="flex gap-2"><span className="text-[#111827]">•</span><span>{typeof x === "string" ? x : JSON.stringify(x)}</span></li>
      ))}
    </ul>
  );
}

export default function DrugDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    api.get(`/drugs/${id}`).then((r) => {
      setD(r.data);
      api.post("/recent", { item_type: "drug", item_id: r.data.drug_id, title: r.data.name }).catch(() => {});
    });
    api.get("/favorites").then((r) => setFav(r.data.some((f) => f.item_type === "drug" && f.item_id === id))).catch(() => {});
  }, [id]);

  const toggleFav = async () => {
    if (!d) return;
    if (fav) {
      await api.delete(`/favorites/drug/${id}`);
      setFav(false);
      toast.success("Removed");
    } else {
      await api.post("/favorites", { item_type: "drug", item_id: id, title: d.name });
      setFav(true);
      toast.success("Saved");
    }
  };

  if (!d) return <div className="text-slate-500 text-sm">Loading...</div>;
  const ix = d.interactions_detail || [];

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="drug-detail">
      <Link to="/app/drugs" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to drug reference
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {d.category && <Badge className="bg-[#EFF4FF] text-[#111827] border-0">{d.category}</Badge>}
            {d.drug_class && <span className="text-sm text-slate-500">{d.drug_class}</span>}
            <ReviewStatusBadge status={d.review_status} reviewedAt={d.reviewed_at} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#111827] flex items-center gap-2">
            <Pill className="w-7 h-7 text-[#111827]" /> {d.name}
          </h1>
          {d.indication && <p className="text-slate-600 mt-2">{d.indication}</p>}
        </div>
        <Button onClick={toggleFav} variant="outline" className={fav ? "border-amber-300 text-amber-700 bg-amber-50" : ""} data-testid="favorite-toggle">
          <Star className={`w-4 h-4 mr-2 ${fav ? "fill-amber-400" : ""}`} />
          {fav ? "Favorited" : "Add favorite"}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6 grid md:grid-cols-2 gap-5">
          <Field label="Adult dose" value={d.adult_dose} />
          <Field label="Paediatric dose" value={d.paediatric_dose} />
          <Field label="Weight-based" value={d.weight_based_calculation} />
          <Field label="Route" value={d.route} />
          <Field label="Frequency" value={d.frequency} />
          <Field label="Max dose" value={d.max_dose} />
          <Field label="Renal adjustment" value={d.renal_adjustment} />
          <Field label="Pregnancy / breastfeeding" value={d.pregnancy_lactation} />
          <Field label="Interactions" value={typeof d.interactions === "string" ? d.interactions : null} />
        </CardContent>
      </Card>

      {Array.isArray(d.contraindications) && d.contraindications.length > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
              <h3 className="font-heading font-semibold text-rose-900">Contraindications</h3>
            </div>
            <BulletList items={d.contraindications} />
          </CardContent>
        </Card>
      )}

      {Array.isArray(d.side_effects) && d.side_effects.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold text-[#111827] mb-2">Side effects</h3>
            <BulletList items={d.side_effects} />
          </CardContent>
        </Card>
      )}

      {Array.isArray(d.serious_side_effects) && d.serious_side_effects.length > 0 && (
        <Card className="border-rose-200 bg-rose-50/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
              <h3 className="font-heading font-semibold text-rose-900">Serious adverse effects</h3>
            </div>
            <BulletList items={d.serious_side_effects} />
          </CardContent>
        </Card>
      )}

      {Array.isArray(d.monitoring) && d.monitoring.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold text-[#111827] mb-2">Monitoring</h3>
            <BulletList items={d.monitoring} />
          </CardContent>
        </Card>
      )}

      {ix.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold text-[#111827] mb-3">Known interactions ({ix.length})</h3>
            <div className="space-y-2">
              {ix.map((i, n) => (
                <div key={n} className={`border rounded-md p-3 text-sm ${sevClass(i.severity)}`}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="font-semibold">{i.interacting_drug}</div>
                    <span className="text-[10px] uppercase tracking-wide font-semibold">{i.severity}</span>
                  </div>
                  <div className="text-xs mb-1">{i.clinical_effect}</div>
                  <div className="text-xs font-medium">→ {i.recommendation}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {Array.isArray(d.references) && d.references.length > 0 && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2 text-slate-700">
              <BookOpen className="w-4 h-4" />
              <h3 className="font-heading font-semibold text-sm">References</h3>
            </div>
            <ul className="text-xs text-slate-600 space-y-1">
              {d.references.map((r, i) => <li key={i}>{i + 1}. {r}</li>)}
              {d.guideline_source && <li className="pt-1 border-t border-slate-200 font-medium">Source: {d.guideline_source}</li>}
            </ul>
          </CardContent>
        </Card>
      )}

      <GuidelineReviewNotice source={d.guideline_source} />
    </div>
  );
}
